import { useMemo } from 'react';
import { GroupedSelect } from './ui/GroupedSelect';

interface Status {
  id: string;
  name: string;
  parentId?: string | null;
  children?: Status[];
}

// Helper to build status tree
function buildStatusTree(statuses: Status[]): Status[] {
  const map = new Map<string, Status>();
  const roots: Status[] = [];

  statuses.forEach(status => {
    map.set(status.id, { ...status, children: [] });
  });

  statuses.forEach(status => {
    const node = map.get(status.id)!;
    if (status.parentId && map.has(status.parentId)) {
      map.get(status.parentId)!.children!.push(node);
    } else {
      roots.push(node);
    }
  });

  return roots;
}

// Flatten tree to options with indentation
function flattenStatusTree(statuses: Status[], level = 0): Array<{ id: string; name: string; level: number }> {
  const result: Array<{ id: string; name: string; level: number }> = [];

  statuses.forEach(status => {
    result.push({ id: status.id, name: status.name, level });
    if (status.children && status.children.length > 0) {
      result.push(...flattenStatusTree(status.children, level + 1));
    }
  });

  return result;
}

// Status select grouped per top-level (store/category)
export function StatusTreeSelect({
  id,
  value,
  onChange,
  statuses,
  placeholder = '-- Wybierz status --',
}: {
  id?: string;
  value: string;
  onChange: (value: string) => void;
  statuses: Status[];
  placeholder?: string;
}) {
  const statusTree = useMemo(() => buildStatusTree(statuses), [statuses]);

  const { storeRoots, orphanRoots } = useMemo(() => {
    const stores: Status[] = [];
    const orphans: Status[] = [];
    statusTree.forEach((root) => {
      if (root.children && root.children.length > 0) {
        stores.push(root);
      } else {
        orphans.push(root);
      }
    });
    return { storeRoots: stores, orphanRoots: orphans };
  }, [statusTree]);

  // Indent zaczyna się od level 1 - root (poziom 0) ma już własny optgroup label.
  const getIndent = (level: number): string => {
    if (level === 0) return '';
    const nbsp = '\u00A0';
    const indent = (nbsp.repeat(6)).repeat(level - 1);
    return `${indent}└─ `;
  };

  return (
    <GroupedSelect id={id} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="">{placeholder}</option>
      {storeRoots.map((store) => {
        const flat = flattenStatusTree([store]);
        return (
          <optgroup key={store.id} label={store.name.toUpperCase()}>
            {flat.map((s) => (
              <option key={s.id} value={s.id}>
                {getIndent(s.level)}{s.name}
              </option>
            ))}
          </optgroup>
        );
      })}
      {orphanRoots.length > 0 && (
        <optgroup label="INNE">
          {orphanRoots.map((s) => (
            <option key={s.id} value={s.id}>{s.name}</option>
          ))}
        </optgroup>
      )}
    </GroupedSelect>
  );
}
